// File: src/pages/OfflinePlaybackPage.js
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { getFirestore, doc, onSnapshot, getDoc, collection, getDocs } from "firebase/firestore";
import { useProfile } from "../context/ProfileContext";
import { useMusic } from "../context/MusicContext";
import { usePlaylistArtwork } from "../hooks/usePlaylistArtwork";
import { saveEncryptedTrack, getDecryptedTrackBlob } from "../services/offlineStorage";
import toast from "react-hot-toast";

const OfflinePlaybackPage = () => {
  const { activeProfile, isDarkMode } = useProfile();
  const { setQueueAndPlay, currentSong } = useMusic();

  const [offlineDoc, setOfflineDoc] = useState({ songIds: [] });
  const [songs, setSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [cachedStatus, setCachedStatus] = useState({});
  const [cachingIds, setCachingIds] = useState({});

  // 1. Listen to single Offline Playback document
  useEffect(() => {
    if (!activeProfile?.id) {
      setIsLoading(false);
      return;
    }

    const db = getFirestore();
    const docRef = doc(db, "users", activeProfile.id, "specialPlaylists", "offlinePlayback");

    const unsubscribe = onSnapshot(docRef, async (snap) => {
      let songIds = [];
      if (snap.exists()) {
        songIds = snap.data().songIds || [];
        setOfflineDoc(snap.data());
      } else {
        // Fallback for subcollection
        const legacyRef = collection(db, "users", activeProfile.id, "downloadableSongs");
        const legacySnap = await getDocs(legacyRef);
        songIds = legacySnap.docs.map((d) => d.id);
        setOfflineDoc({ songIds });
      }

      if (songIds.length === 0) {
        setSongs([]);
        setIsLoading(false);
        return;
      }

      // Hydrate song documents
      try {
        const songDocs = await Promise.all(
          songIds.map((id) => getDoc(doc(db, "songs", id)))
        );
        const hydrated = songDocs
          .map((s) => (s.exists() ? { id: s.id, ...s.data() } : null))
          .filter((s) => s && s.isDeleted !== true);

        setSongs(hydrated);
      } catch (err) {
        console.error("Error hydrating offline songs:", err);
      } finally {
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, [activeProfile?.id]);

  // Use the 4-artwork playlist hook for the header cover art
  const { artworkUrls, isLoading: isArtworkLoading } = usePlaylistArtwork(offlineDoc);

  // 2. Check local IndexedDB cache status
  useEffect(() => {
    if (songs.length === 0) return;

    const checkCache = async () => {
      const statuses = {};
      for (const s of songs) {
        if (s.id) {
          const blob = await getDecryptedTrackBlob(s.id);
          statuses[s.id] = !!blob;
        }
      }
      setCachedStatus(statuses);
    };

    checkCache();
  }, [songs]);

  // 3. Encrypted In-App Caching (No raw audio export)
  const handleCacheSong = async (song) => {
    if (!song.audioURL) {
      toast.error("Track audio not available.");
      return;
    }

    setCachingIds((prev) => ({ ...prev, [song.id]: true }));
    try {
      const result = await saveEncryptedTrack(song.id, song.audioURL);
      if (result.success) {
        setCachedStatus((prev) => ({ ...prev, [song.id]: true }));
        toast.success(`"${song.title}" cached for Offline Mode!`);
      } else {
        throw new Error(result.error || "Encryption failed.");
      }
    } catch (err) {
      console.error("Cache error:", err);
      toast.error("Failed to cache track offline.");
    } finally {
      setCachingIds((prev) => ({ ...prev, [song.id]: false }));
    }
  };

  // 4. Playback using local blob when available
  const handlePlaySong = useCallback(
    async (clickedIndex) => {
      const formattedQueue = await Promise.all(
        songs.map(async (song) => {
          let targetAudioUrl = song.audioURL;
          const localBlob = await getDecryptedTrackBlob(song.id);
          if (localBlob) {
            targetAudioUrl = localBlob;
          }
          return { ...song, audioURL: targetAudioUrl };
        })
      );

      if (setQueueAndPlay) {
        setQueueAndPlay(formattedQueue, clickedIndex, "Offline Playback");
      }
    },
    [songs, setQueueAndPlay]
  );

  return (
    <div className={`p-4 relative pb-36 min-h-screen ${isDarkMode ? "bg-[#090909] text-white" : "bg-white text-slate-900"}`}>
      {/* Back button */}
      <div className="flex items-center mb-6">
        <Link
          to={`/profile?tab=library`}
          className={`p-2 -m-2 rounded-full transition-colors ${
            isDarkMode ? "text-white hover:bg-zinc-800" : "text-slate-900 hover:bg-slate-100"
          }`}
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
      </div>

      {/* Playlist-Style Cover Art Header */}
      <div className="flex flex-col items-center mb-6 pt-2">
        <div className={`w-40 h-40 rounded-2xl overflow-hidden shadow-2xl mb-4 relative border ${
          isDarkMode ? "border-zinc-800/80 shadow-black/40" : "border-slate-100 shadow-slate-200"
        }`}>
          {isArtworkLoading ? (
            <div className={`w-full h-full ${isDarkMode ? "bg-zinc-900 animate-pulse" : "bg-gray-200 animate-pulse"}`} />
          ) : artworkUrls.length === 4 ? (
            <div className="grid grid-cols-2 grid-rows-2 h-full w-full">
              {artworkUrls.map((url, i) => (
                <img key={i} src={url} alt="Cover" className="w-full h-full object-cover" />
              ))}
            </div>
          ) : (
            <img src={artworkUrls[0] || "/default-artwork.png"} alt="Cover" className="w-full h-full object-cover" />
          )}
        </div>

        <h1 className="text-2xl md:text-3xl font-black italic uppercase tracking-tighter text-center">
          Offline Playback
        </h1>
        <p className={`text-xs font-bold uppercase tracking-widest mt-1 text-center ${isDarkMode ? "text-zinc-500" : "text-gray-400"}`}>
          {songs.length} {songs.length === 1 ? "track" : "tracks"} • Unlocked for Offline Mode
        </p>
      </div>

      {/* Song List */}
      {isLoading ? (
        <div className="text-center py-12 text-xs font-bold uppercase tracking-widest text-gray-400 animate-pulse">
          Loading offline tracks...
        </div>
      ) : songs.length === 0 ? (
        <div className="text-center py-12 space-y-2">
          <p className="text-sm font-semibold text-gray-400">
            No tracks unlocked for offline playback yet.
          </p>
          <p className="text-xs text-[#ff00cc] font-black uppercase tracking-wider">
            Boost 50 points to any track to unlock Offline Mode!
          </p>
        </div>
      ) : (
        <ul className="space-y-1 max-w-2xl mx-auto">
          {songs.map((song, index) => {
            const isCurrentPlaying = currentSong?.id === song.id;
            const isCached = cachedStatus[song.id];
            const isCaching = cachingIds[song.id];

            return (
              <li
                key={song.id}
                className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
                  isDarkMode ? "hover:bg-zinc-900 text-white" : "hover:bg-gray-100 text-slate-900"
                }`}
              >
                {/* Clickable Play Row */}
                <div
                  className="flex items-center flex-grow cursor-pointer min-w-0 pr-3"
                  onClick={() => handlePlaySong(index)}
                >
                  <img
                    src={song.albumArtworkURL || "/default-artwork.png"}
                    alt={song.title}
                    className="w-14 h-14 rounded-xl object-cover mr-4 shadow-md flex-shrink-0"
                  />
                  <div className="flex-grow min-w-0">
                    <p className={`font-bold text-base tracking-tight truncate ${
                      isCurrentPlaying ? "text-[#ff00cc] font-black italic" : ""
                    }`}>
                      {song.title}
                    </p>
                    <p className={`text-xs font-medium truncate ${isDarkMode ? "text-zinc-500" : "text-gray-500"}`}>
                      {song.artistName}
                    </p>
                    <span className="inline-block text-[9px] font-black uppercase tracking-wider text-green-500 mt-0.5">
                      {isCached ? "Encrypted Offline" : "Unlocked for Cache"}
                    </span>
                  </div>
                </div>

                {/* Local Encryption Trigger */}
                <button
                  type="button"
                  onClick={() => handleCacheSong(song)}
                  disabled={isCaching || isCached}
                  title={isCached ? "Encrypted in device storage" : "Cache to device for offline play"}
                  className={`p-2.5 rounded-full transition-all flex-shrink-0 ${
                    isCached
                      ? "text-green-500 cursor-default"
                      : isDarkMode
                      ? "text-zinc-400 hover:text-[#ff00cc] hover:bg-zinc-800"
                      : "text-slate-500 hover:text-[#ff00cc] hover:bg-slate-200"
                  }`}
                >
                  {isCaching ? (
                    <div className="w-5 h-5 border-2 border-[#ff00cc] border-t-transparent rounded-full animate-spin" />
                  ) : isCached ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default OfflinePlaybackPage;